import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuItemOptionProps,
  MenuList,
  MenuOptionGroup,
  Spinner,
  Text,
} from "@chakra-ui/react";
import IconArrowDown from "components/icon/IconArrowDown";
import IconMapFloor from "components/icon/IconMapFloor";
import { PCTooltip } from "components/PCTooltip";
import { SvgIcon } from "components/SvgIcon";
import { WidgetsMode } from "constants/enum";
import { LEVEL_OTHER, LEVEL_OTHER_ID } from "constants/forge";
import useGetCurrentPath from "hooks/useGetCurrentPath";
import useLevel from "hooks/useLevel";
import useTextHover from "hooks/useTextHover";
import { Level } from "interfaces/models";
import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "redux/store";
import { routePath } from "routes/path";

interface Props {
  currentWidgetActive?: WidgetsMode;
  levelSelectedLabel?: string;
  isMobile?: boolean;
  onOpenProjectDashboard?: () => void;
  onOpenUserDashboard?: () => void;
  onChangeProject?: (id: any) => void;
  onChangeFloor?: (guid: any) => void;
  isLargeScreenForgeViewer?: Boolean;
}

const RenderLevelOptionItem = ({
  isSelected,
  label,
  ...rest
}: MenuItemOptionProps & { label: string; isSelected: boolean }) => {
  const { hoverStatus, textElementRef } = useTextHover();

  return (
    <MenuItemOption
      {...rest}
      className="text-ellipsis"
      fontSize={{
        base: "1.4rem",
        sm: "1.3rem",
        xxl: "1.4rem",
      }}
      fontWeight="medium"
      fontFamily="NotoSansCJKjp"
      p=".5rem 1.5rem"
      borderBottom="1px solid var(--primary-border-color)"
      _last={{ borderBottom: "none" }}
      _hover={{ bg: "#F0F9FF" }}
      _focus={{ bg: "#F0F9FF" }}
      icon={undefined}
      sx={{
        "& > span:first-of-type": {
          display: "none",
        },
        "& > span:nth-of-type(2)": {
          width: "100%",
        },
      }}
    >
      <PCTooltip label={label} isDisabled={!hoverStatus}>
        <Flex alignItems="center" gap="0.5rem" width="100%">
          <Box flexShrink={0} width="1.6rem" height="1.6rem">
            {isSelected && (
              <SvgIcon
                src="/img/icon-action-check_circle.svg"
                width="1.6rem"
                height="1.6rem"
              />
            )}
          </Box>
          <Text ref={textElementRef} className="text-ellipsis">
            {label}
          </Text>
        </Flex>
      </PCTooltip>
    </MenuItemOption>
  );
};

function ForgeHeaderItem({
  levelSelectedLabel,
  isMobile,
  isLargeScreenForgeViewer,
}: Props) {
  const { params, pathPattern } = useGetCurrentPath();
  const navigate = useNavigate();

  const { projectDetail, dataProjectDetail } = useSelector(
    (state: RootState) => state.project
  );
  const { isOnline, syncDataOption } = useSelector(
    (state: RootState) => state.app
  );

  const { isLoadedViewerModelData } = useSelector(
    (state: RootState) => state.forgeViewer
  );

  const isForgePage = useMemo(
    () => pathPattern === routePath.ForgeViewer,
    [pathPattern]
  );

  const {
    levelSelected,
    levels,
    isWaittingCaptureKeynote,
    isGeneratingFamilyInstances,
  } = useSelector((state: RootState) => state.forgeViewer);

  const isDisabledLevelOther = useMemo(() => {
    if (!dataProjectDetail?.id || isOnline) {
      return false;
    }

    const modelCached =
      (syncDataOption?.mapModelCached || {})?.[dataProjectDetail?.id || ""]?.[
        LEVEL_OTHER.label || ""
      ] || {};

    return !modelCached.modelType?.length;
  }, [isOnline, syncDataOption, dataProjectDetail?.id]);

  const levelSelectedRef = useRef(levelSelected);
  useEffect(() => {
    levelSelectedRef.current = levelSelected;
  }, [levelSelected]);

  const { handleChangeFloor } = useLevel(dataProjectDetail?.id);

  const change = (guid: string | string[]) => {
    if (guid === levelSelectedRef?.current?.guid) {
      return;
    }
    const level = handleChangeFloor(guid);
    levelSelectedRef.current = level!;
  };

  return (
    <>
      <Flex
        alignItems={"center"}
        flexDir={"row"}
        gap={{
          base: "0.5rem",
          lg: isLargeScreenForgeViewer ? "0.8rem" : "3.2rem",
        }}
      >
        <Box>
          <Breadcrumb
            spacing={{ base: 0, xl: "0.8rem" }}
            separator={<IconArrowDown width="24px" height="24px" />}
            sx={{
              ol: {
                display: "flex",
                alignItems: "center",
                li: {
                  ".chakra-breadcrumb__link": {
                    display: "block",
                  },
                },
              },
            }}
            color="font.default"
          >
            <BreadcrumbItem minW={{ base: "25%", lg: "20%", xl: "auto" }}>
              <BreadcrumbLink
                onClick={() => {
                  navigate(routePath.Home);
                }}
              >
                プロジェクト一覧
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem minW={{ base: "30%", xl: "auto" }}>
              <BreadcrumbLink
                onClick={() => {
                  navigate(`${routePath.Home}#project-${projectDetail.id}`);
                }}
              >
                {projectDetail?.name ?? <Spinner />}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink
                _hover={{ textDecoration: "none", cursor: "default" }}
              >
                {dataProjectDetail?.name ?? <Spinner />}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
        </Box>

        <Menu isOpen={isForgePage ? undefined : false}>
          {({ isOpen }) => (
            <>
              <MenuButton
                className="btn-level-selected"
                as={Button}
                isDisabled={
                  !params?.bimFileId ||
                  isGeneratingFamilyInstances ||
                  isWaittingCaptureKeynote ||
                  !isLoadedViewerModelData
                }
                variant="whiteIcon"
                minW="11rem"
                w={{
                  base: "18rem",
                  sm: "14rem",
                  "2xl": "18rem",
                }}
                fontSize={{ base: "1.4rem", sm: "1.3rem", xxl: "1.4rem" }}
                mt={isMobile ? "1rem" : 0}
                mr="1rem !important"
                height="4rem"
                border="1px solid #A3A3A3 !important"
                padding={{
                  base: "0.8rem 1.6rem",
                  lg: "0.8rem 1rem",
                  "2xl": "0.8rem 1.6rem",
                }}
                textAlign={params?.bimFileId ? "center" : "left"}
                boxShadow="none"
                sx={{
                  "&:focus": {
                    boxShadow: "none",
                  },
                  "&[disabled]": {
                    opacity: "0.5 !important",
                  },
                  "span:nth-of-type(2)": {
                    base: {
                      flex: "unset",
                      mr: "auto",
                    },
                    sm: {
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    },
                  },
                  mr: 0,
                  ...(!isForgePage
                    ? {
                        backgroundColor: "#FFFFFF61",
                        opacity: 0.4,
                        cursor: "not-allowed",
                      }
                    : {}),
                }}
                leftIcon={
                  !levelSelectedLabel && isForgePage ? (
                    <Spinner size="md" color="blue.500" />
                  ) : params?.bimFileId && isForgePage ? (
                    <IconMapFloor width="2.4rem" height="2.4rem" />
                  ) : undefined
                }
                rightIcon={
                  params?.bimFileId && levelSelectedLabel ? (
                    <IconArrowDown
                      transition="0.4s"
                      width="2.4rem"
                      height="2.4rem"
                      transform={isOpen ? "rotate(270deg)" : "rotate(90deg)"}
                    />
                  ) : undefined
                }
              >
                {params?.bimFileId ? levelSelectedLabel : "-"}
              </MenuButton>
              <MenuList
                w={{
                  base: "18rem",
                  sm: "14rem",
                  "2xl": "18rem",
                }}
                maxH={{
                  base: "48rem",
                  sm: "34rem",
                  "2xl": "48rem",
                }}
                overflow="auto"
                zIndex="popover"
              >
                <MenuOptionGroup
                  defaultValue={levelSelected.guid || ""}
                  title=""
                  type="radio"
                  value={levelSelected.guid || ""}
                  onChange={handleChangeFloor}
                >
                  {levels
                    ?.filter(
                      (level: Level) =>
                        level.guid &&
                        (level.guid !== LEVEL_OTHER_ID || level.sheets?.length)
                    )
                    .map((d, i) => (
                      <RenderLevelOptionItem
                        key={i}
                        value={d.guid}
                        label={d.label}
                        isDisabled={
                          d.guid === LEVEL_OTHER_ID
                            ? isDisabledLevelOther
                            : false
                        }
                        isSelected={d.guid === levelSelected.guid}
                        onClick={() => {
                          change(d.guid);
                        }}
                      />
                    ))}
                </MenuOptionGroup>
              </MenuList>
            </>
          )}
        </Menu>
      </Flex>
    </>
  );
}

export default ForgeHeaderItem;
